import {
  ChronoField, ChronoUnit, DateTimeFormatter, DateTimeFormatterBuilder,
  LocalDate, LocalDateTime, ResolverStyle,
} from '@js-joda/core';

/** Календарные дата и время без зоны. Расчёты не зависят от перевода часов в браузере. */
export class LocalDateTimeUtil {
  /** Строгий разбор даты и времени без зоны; неверные даты и лишние символы вызывают ошибку. */
  public static fromFormattedString(value: string, pattern: string): LocalDateTime {
    const formatter = new DateTimeFormatterBuilder().appendPattern(pattern)
      .parseDefaulting(ChronoField.ERA, 1).toFormatter().withResolverStyle(ResolverStyle.STRICT);
    const result = LocalDateTime.parse(value, formatter);
    if (result.format(formatter) !== value) {
      throw new Error('Date-time does not match the requested format');
    }
    return result;
  }

  /** Начало календарного дня без часового пояса. */
  public static startOfDay(date: LocalDate): LocalDateTime {
    return date.atStartOfDay();
  }

  /** Конец календарного дня с миллисекундной точностью прежних периодов Dayjs. */
  public static endOfDay(date: LocalDate): LocalDateTime {
    return date.atTime(23, 59, 59, 999000000);
  }

  /**
   * ISO-строка локальных полей без зоны и долей секунды.
   * Сохраняет краткую запись HH:mm при нулевых секундах — контракт фильтров логистики.
   */
  public static toIsoSecondsString(value: LocalDateTime): string {
    return value.withNano(0).toString();
  }

  /** Начало дня плюс секунды; значения за пределами суток переходят на соседние даты. */
  public static fromDateAndSeconds(date: LocalDate, seconds: number): LocalDateTime {
    return date.atStartOfDay().plusSeconds(seconds);
  }

  /** Сдвигает на календарные дни с сохранением времени, включая доли секунды. */
  public static addDays(value: LocalDateTime, days: number): LocalDateTime {
    return value.plusDays(days);
  }

  /** Заменяет дату, сохраняя время суток. */
  public static withDate(value: LocalDateTime, date: LocalDate): LocalDateTime {
    return date.atTime(value.toLocalTime());
  }

  /** Первая дата со временем строго раньше второй. */
  public static isBefore(value: LocalDateTime, other: LocalDateTime): boolean {
    return value.isBefore(other);
  }

  /** Сравнивает значения; два отсутствующих значения считаются равными. */
  public static isEqual(value: LocalDateTime | null, other: LocalDateTime | null): boolean {
    return value === other || (!!value && !!other && value.equals(other));
  }

  /** Полные календарные секунды со знаком; переходы часовых поясов не учитываются. */
  public static secondsBetween(from: LocalDateTime, to: LocalDateTime): number {
    return from.until(to, ChronoUnit.SECONDS);
  }

  /** Шаблон js-joda; по умолчанию строка API без зоны и долей секунды. */
  public static toFormattedString(
    value: LocalDateTime | null | undefined,
    pattern = "yyyy-MM-dd'T'HH:mm:ss",
  ): string {
    return value ? value.format(DateTimeFormatter.ofPattern(pattern)) : '';
  }
}
